import { StrictMode, useEffect } from "react";
import { createRoot } from "react-dom/client";
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from "react-router-dom";
import * as Sentry from "@sentry/react";
import App from "./App";
import { sentryScrubIntegration, sentryScrubOptions } from "./lib/sentry";
import "./styles.css";

if (import.meta.env.VITE_SENTRY_DSN) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment:
      import.meta.env.VITE_SENTRY_ENV ?? import.meta.env.MODE,
    release: import.meta.env.VITE_BUILD_VERSION,
    tracesSampleRate: 0.1,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,
    sendDefaultPii: false,
    // The free-text scrub (Kanzlei K4-B): beforeSend / beforeBreadcrumb /
    // beforeSendTransaction / beforeSendSpan, and the integration whose
    // processEvent reaches every event type. beforeSendSpan is the only hook
    // an INP span passes — its name is the clicked element's path.
    // The spread also sets maxValueLength 8192: the SDK cuts texts to 250
    // characters before any hook runs, and a cut text loses the suffix the
    // patterns anchor on. Nothing after the spread may set one of its keys
    // (sentry.wiring.test.ts).
    //
    // A published event site runs a FROZEN copy of this bundle from R2
    // (`landing-pages/{eventId}/`): this reaches a live event only after the
    // site-templates sync AND that event's republish
    // (main-backend/docs/backfill-landing-pages.md).
    ...sentryScrubOptions(),
    integrations: [
      sentryScrubIntegration(),
      Sentry.reactRouterV7BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
    ],
  });
}

function AppCrash() {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "100dvh",
        padding: "3rem 2rem",
        fontFamily: "Inter, system-ui, sans-serif",
        background: "#ffffff",
        color: "#121212",
        textAlign: "center",
        gap: "1rem",
      }}
    >
      <h1 style={{ fontSize: "1.5rem", fontWeight: 600, margin: 0 }}>
        Something went wrong.
      </h1>
      <p style={{ color: "#5d5d5d", margin: 0 }}>
        Please reload the page. If the problem persists, contact support.
      </p>
      <button
        type="button"
        onClick={() => window.location.reload()}
        style={{
          marginTop: "0.5rem",
          padding: "0.625rem 1.25rem",
          borderRadius: "0.5rem",
          border: "none",
          background: "#222222",
          color: "#ffffff",
          fontSize: "0.875rem",
          fontWeight: 500,
          cursor: "pointer",
        }}
      >
        Reload
      </button>
    </div>
  );
}

const rootElement = document.getElementById("root");
if (!rootElement) {
  throw new Error("Root element not found.");
}

createRoot(rootElement).render(
  <StrictMode>
    <Sentry.ErrorBoundary fallback={<AppCrash />}>
      <App />
    </Sentry.ErrorBoundary>
  </StrictMode>,
);
